// Season Wrapped aggregator — the orchestrator.
//
// Takes a team's season (team + players + games + at-bats) and produces a fully
// LOCKED SeasonWrappedData: every number computed here, deterministic template
// copy applied as the floor, and a smart-curated slide list (only the slides this
// team has data for). The season-wrapped endpoint later overwrites the prose
// fields with AI narration; if it never returns, this output already renders a
// complete Wrapped.
//
// Reuse: the per-player rollup is the SHARED buildSeasonStats (so Wrapped and the
// Stats tab never disagree). The team record / run-diff / one-run logic mirrors
// StatsView's `coachsEyePayload` exactly.

import type {
  AtBatDoc,
  SeasonWrappedData,
  WrappedSlide,
  WrappedMoments,
} from '../../types/dugout';
import { buildSeasonStats } from '../season-stats';
import { fieldTimeSourceOf } from '../gc-innings';
import { computeBiggestMoments } from './biggest-moment';
import { assignPlayerSuperlatives } from './player-superlatives';
import {
  templateThesis,
  templateClosing,
  templateSuperlativeProse,
  templateBiggestMomentCall,
  templateClutchProse,
  templateMoundProse,
  TRANSITIONS,
} from './wrapped-templates';

export interface AggregatorInput {
  team: any;
  players: any[];
  /** The season's games (caller filters to the season via gamesForSeason). */
  games: any[];
  /** All at-bats across the season's games, each tagged with gameId. */
  atBats: AtBatDoc[];
  isHistoryMode?: boolean;
}

const fmtRate = (n: number | null | undefined) =>
  n != null && isFinite(n) ? n.toFixed(3).replace(/^0/, '') : '—';
const fmtERA = (n: number | null | undefined) =>
  n != null && isFinite(n) ? n.toFixed(2) : '—';
const fmtIP = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Longest run of wins across the season's results (chronological). */
function longestWinStreak(resultGames: any[]): number {
  let best = 0, cur = 0;
  for (const g of resultGames) {
    if (g.result?.outcome === 'W') { cur++; best = Math.max(best, cur); }
    else cur = 0;
  }
  return best;
}

export function buildSeasonWrapped(input: AggregatorInput): SeasonWrappedData {
  const { team, players, games, atBats, isHistoryMode } = input;

  const season = team?.season || 'Season';
  const seasonYear = team?.seasonYear ?? new Date().getFullYear();
  const seasonKey = `${seasonYear}-${season}`;

  // ── Shared per-player rollup (single source of truth) ──────────────────────
  const { stats } = buildSeasonStats(players, games, { isHistoryMode, fieldTimeSource: fieldTimeSourceOf(team), defaultInnings: team?.defaultInnings });
  const rosterNameById = new Map<string, string>(players.map(p => [p.id, p.name]));

  // ── Team record + run differential (mirrors StatsView.coachsEyePayload) ────
  const resultGames = games
    .filter(g => g.status === 'completed' && g.result)
    .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
  const wins = resultGames.filter(g => g.result.outcome === 'W').length;
  const losses = resultGames.filter(g => g.result.outcome === 'L').length;
  const ties = resultGames.filter(g => g.result.outcome === 'T').length;
  const runsFor = resultGames.reduce((s, g) => s + (g.result.myScore ?? 0), 0);
  const runsAgainst = resultGames.reduce((s, g) => s + (g.result.opponentScore ?? 0), 0);
  const oneRunGames = resultGames.filter(
    g => Math.abs((g.result.myScore ?? 0) - (g.result.opponentScore ?? 0)) === 1,
  );
  const oneRunRecord = `${oneRunGames.filter(g => g.result.outcome === 'W').length}-${oneRunGames.filter(g => g.result.outcome === 'L').length}`;

  // ── Team highlights ────────────────────────────────────────────────────────
  const shutouts = resultGames.filter(g => g.result.outcome === 'W' && (g.result.opponentScore ?? 1) === 0).length;
  const streak = longestWinStreak(resultGames);
  const biggestWin = resultGames
    .filter(g => g.result.outcome === 'W')
    .map(g => ({ margin: (g.result.myScore ?? 0) - (g.result.opponentScore ?? 0), g }))
    .sort((a, b) => b.margin - a.margin)[0];

  // ── Total innings managed (from inning logs) ───────────────────────────────
  let totalInnings = 0;
  games.forEach(g => {
    if (g.status === 'completed' && g.inningLogs) {
      totalInnings += Object.keys(g.inningLogs).length;
    }
  });

  // ── Coach proxies ──────────────────────────────────────────────────────────
  const distinctLineups = new Set(
    games.filter(g => Array.isArray(g.lineup) && g.lineup.length).map(g => (g.lineup as string[]).join('|')),
  ).size;
  // Pitching changes = (distinct pitchers used per game) - 1, summed, floored at 0.
  let pitchingChanges = 0;
  const pitchersByGame = new Map<string, Set<string>>();
  atBats.forEach(ab => {
    if (ab.pitcherTeam !== 'us') return;
    const key = ab.gameId;
    const set = pitchersByGame.get(key) || new Set<string>();
    set.add(ab.pitcherId ?? ab.pitcherName ?? '?');
    pitchersByGame.set(key, set);
  });
  pitchersByGame.forEach(set => { pitchingChanges += Math.max(0, set.size - 1); });
  // Tournaments
  const tournamentGameIds = games.filter(g => g.tournamentId && g.status === 'completed' && g.result);
  const tournamentIds = new Set(tournamentGameIds.map(g => g.tournamentId));
  const tW = tournamentGameIds.filter(g => g.result.outcome === 'W').length;
  const tL = tournamentGameIds.filter(g => g.result.outcome === 'L').length;
  // Game balls awarded (live recipients across the season)
  const gameBallsAwarded = games.reduce((n, g) => n + ((g.gameBallRecipients || []).length), 0);

  // ── Engines: biggest moments + per-player superlatives ─────────────────────
  const momentResult = computeBiggestMoments(atBats, rosterNameById);
  const superlatives = assignPlayerSuperlatives({ stats, players, games, atBats, moments: momentResult });

  // Apply template prose (the floor) to superlatives.
  superlatives.forEach(s => { s.prose = templateSuperlativeProse(s); });

  // ── Assemble the moments block with template prose ─────────────────────────
  const moments: WrappedMoments = {
    biggestMoment: momentResult.biggest
      ? {
          playerId: momentResult.biggest.playerId,
          playerName: momentResult.biggest.playerName,
          description: momentResult.biggest.description,
          prose: templateBiggestMomentCall(momentResult.biggest.playerName, momentResult.biggest.description),
        }
      : null,
    clutchPlayer: momentResult.clutchPlayer
      ? {
          playerId: momentResult.clutchPlayer.playerId,
          playerName: momentResult.clutchPlayer.playerName,
          statLine: `${momentResult.clutchPlayer.moments} clutch moments`,
          prose: templateClutchProse(momentResult.clutchPlayer.playerName, `${momentResult.clutchPlayer.moments} clutch moments`),
        }
      : null,
    topLeverageInning: momentResult.topLeverageInning
      ? {
          playerId: momentResult.topLeverageInning.playerId,
          playerName: momentResult.topLeverageInning.playerName,
          description: momentResult.topLeverageInning.description,
          prose: templateMoundProse(momentResult.topLeverageInning.playerName, momentResult.topLeverageInning.description),
        }
      : null,
  };

  // ── Batting / pitching leader rows (for the leaders slides) ────────────────
  const leaderRow = (
    label: string,
    value: (s: any) => number | null | undefined,
    gate: (s: any) => boolean,
    fmt: (n: number) => string,
  ) => {
    let best: { name: string; v: number } | null = null;
    for (const s of Object.values(stats) as any[]) {
      if (!gate(s)) continue;
      const v = value(s);
      if (v == null || !isFinite(v)) continue;
      if (!best || v > best.v) best = { name: s.player.name, v };
    }
    return best ? { label, playerName: best.name, value: fmt(best.v) } : null;
  };

  const battingRows = [
    leaderRow('Average', s => s.batting.avg, s => (s.batting.pa || 0) >= 8, fmtRate),
    leaderRow('OPS', s => s.batting.ops, s => (s.batting.pa || 0) >= 8, fmtRate),
    leaderRow('Hits', s => s.batting.h, s => (s.batting.h || 0) > 0, n => String(n)),
    leaderRow('RBI', s => s.batting.rbi, s => (s.batting.rbi || 0) > 0, n => String(n)),
  ].filter(Boolean) as { label: string; playerName: string; value: string }[];

  const pitchingRows = [
    leaderRow('Innings', s => s.pitching.ip, s => (s.pitching.ip || 0) > 0, fmtIP),
    leaderRow('Strikeouts', s => s.pitching.so, s => (s.pitching.so || 0) > 0, n => String(n)),
    leaderRow('ERA', s => (s.pitching.era != null ? -s.pitching.era : null), s => (s.pitching.ip || 0) >= 4, n => fmtERA(-n)),
  ].filter(Boolean) as { label: string; playerName: string; value: string }[];

  // ── Build the data object (numbers locked) ─────────────────────────────────
  const data: SeasonWrappedData = {
    teamName: team?.name || 'Our Team',
    ageGroup: team?.ageGroup || '',
    season,
    seasonYear,
    seasonKey,
    generatedAt: new Date().toISOString(),
    record: { wins, losses, ties },
    runDiff: { runsFor, runsAgainst, diff: runsFor - runsAgainst, oneRunRecord },
    gamesPlayed: resultGames.length,
    totalInnings,
    moments,
    superlatives,
    thesis: '',        // filled below (template), AI overwrites
    closingNote: '',
    slides: [],
    aiNarrated: false,
  };
  data.thesis = templateThesis(data);
  data.closingNote = templateClosing(data);

  // ── Smart-curated slide list ───────────────────────────────────────────────
  const slides: WrappedSlide[] = [];
  slides.push({
    kind: 'intro',
    title: `${data.teamName}`,
    subtitle: `${season} ${seasonYear} Wrapped`,
  });

  if (resultGames.length > 0) {
    slides.push({ kind: 'record', wins, losses, ties, thesis: data.thesis });
    slides.push({
      kind: 'runDiff',
      runsFor,
      runsAgainst,
      diff: runsFor - runsAgainst,
      oneRunRecord,
      transition: TRANSITIONS.toRunDiff,
    });
  }

  // Highlights — only if at least one notable line exists.
  const highlightLines: string[] = [];
  if (streak >= 3) highlightLines.push(`${streak}-game win streak`);
  if (shutouts > 0) highlightLines.push(`${shutouts} shutout${shutouts === 1 ? '' : 's'} thrown`);
  if (biggestWin && biggestWin.margin >= 5) {
    highlightLines.push(`Biggest win: ${biggestWin.g.result.myScore}-${biggestWin.g.result.opponentScore} over ${biggestWin.g.opponent || 'opponent'}`);
  }
  if (highlightLines.length) {
    slides.push({ kind: 'highlights', lines: highlightLines, transition: TRANSITIONS.toHighlights });
  }

  if (battingRows.length) {
    slides.push({ kind: 'battingLeaders', rows: battingRows, transition: TRANSITIONS.toBatting });
  }
  if (pitchingRows.length) {
    slides.push({ kind: 'pitchingLeaders', rows: pitchingRows, transition: TRANSITIONS.toPitching });
  }

  // Biggest Moment + Clutch (the emotional peak).
  if (moments.biggestMoment) {
    slides.push({ kind: 'biggestMoment', moment: moments.biggestMoment });
  }
  if (moments.clutchPlayer) {
    slides.push({
      kind: 'clutchPlayer',
      clutch: moments.clutchPlayer,
      footnote: 'Clutch is our leverage score: late innings and run-scoring at-bats weigh most. Not an official stat.',
    });
  }

  // Per-player superlative cards (whole roster where an honest hook exists).
  for (const s of superlatives) {
    slides.push({ kind: 'superlative', superlative: s });
  }

  // Coach card.
  const coachRows = [
    { label: 'Innings managed', value: String(totalInnings) },
    { label: 'Lineups built', value: String(distinctLineups) },
    { label: 'Pitching changes', value: String(pitchingChanges) },
    tournamentIds.size > 0 ? { label: 'Tournaments', value: `${tournamentIds.size} (${tW}-${tL})` } : null,
    gameBallsAwarded > 0 ? { label: 'Game balls given', value: String(gameBallsAwarded) } : null,
  ].filter(Boolean) as { label: string; value: string }[];
  if (coachRows.some(r => r.value !== '0')) {
    slides.push({ kind: 'coachCard', rows: coachRows, transition: TRANSITIONS.toCoach });
  }

  slides.push({ kind: 'closing', note: data.closingNote });

  data.slides = slides;
  return data;
}
